import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { VersionChecker } from "@/components/VersionChecker";

import { RealtimeProvider } from "@/components/RealtimeProvider";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import { CoachAuthProvider } from "@/hooks/useCoachAuth";
import { ParentAuthProvider } from "@/hooks/useParentAuth";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Retry wrapper for lazy imports to handle stale chunks after deploys
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return React.lazy(() =>
    factory().catch(() => {
      // Chunk failed to load (stale deploy) — reload the page once
      const key = 'chunk-retry-refresh';
      const hasRefreshed = sessionStorage.getItem(key);
      if (!hasRefreshed) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render an error
        return new Promise(() => {});
      }
      sessionStorage.removeItem(key);
      // If we already retried, let the error propagate
      return factory();
    })
  );
}

// Player components
const PlayerLayout = lazyWithRetry(() => import("./components/PlayerLayout"));
const PlayerDashboard = lazyWithRetry(() => import("./pages/PlayerDashboard"));
const PlayerSession = lazyWithRetry(() => import("./pages/PlayerSession"));
// PlayerProfile removed - merged into PlayerAvatar
const PlayerTrain = lazyWithRetry(() => import("./pages/PlayerTrain"));
const PlayerSubcategoryDetail = lazyWithRetry(() => import("./pages/PlayerSubcategoryDetail"));
const PlayerSkillStrategy = lazyWithRetry(() => import("./pages/PlayerSkillStrategy"));
const PlayerCustomStrategy = lazyWithRetry(() => import("./pages/PlayerCustomStrategy"));
const PlayerSkillStrategyActive = lazyWithRetry(() => import("./pages/PlayerSkillStrategyActive"));
const PlayerAvatar = lazyWithRetry(() => import("./pages/PlayerAvatar"));
const PlayerLockerRoom = lazyWithRetry(() => import("./pages/PlayerLockerRoom"));
const PlayerCareerRoadmap = lazyWithRetry(() => import("./pages/PlayerCareerRoadmap"));
const PlayerCampLeaderboard = lazyWithRetry(() => import("./pages/PlayerCampLeaderboard"));
const PlayerProgram = lazyWithRetry(() => import("./pages/PlayerProgram"));
const PlayerSoccerIQ = lazyWithRetry(() => import("./pages/PlayerSoccerIQ"));
const PlayerSoccerIQDetail = lazyWithRetry(() => import("./pages/PlayerSoccerIQDetail"));
const PlayerSoccerIQSession = lazyWithRetry(() => import("./pages/PlayerSoccerIQSession"));

// Lazy load pages to avoid potential circular dependencies
const CoachAdmin = lazyWithRetry(() => import("./pages/CoachAdmin"));
const CoachAuth = lazyWithRetry(() => import("./pages/CoachAuth"));
const CoachTestPage = lazyWithRetry(() => import("./pages/CoachTestPage"));

// Coach components  
const CoachLayout = lazyWithRetry(() => import("./components/CoachLayout"));
const CoachDashboard = lazyWithRetry(() => import("./pages/CoachDashboard"));
const CoachPlayerManagement = lazyWithRetry(() => import("./pages/CoachPlayerManagement"));
const CoachTeamManagement = lazyWithRetry(() => import("./pages/CoachTeamManagement"));
const CoachSkillsStrategy = lazyWithRetry(() => import("./pages/CoachSkillsStrategy"));
const CoachSettings = lazyWithRetry(() => import("./pages/CoachSettings"));
const Galleries = lazyWithRetry(() => import("./pages/Galleries"));
const S3FolderDetail = lazyWithRetry(() => import("./pages/S3FolderDetail"));
const GalleryDetail = lazyWithRetry(() => import("./pages/GalleryDetail"));

// Admin components
import AdminAuth from "./pages/AdminAuth";
const AdminLayout = lazyWithRetry(() => import("./components/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const AdminClubs = lazyWithRetry(() => import("./pages/AdminClubs"));
const AdminUsers = lazyWithRetry(() => import("./pages/AdminUsers"));
const AdminApprovals = lazyWithRetry(() => import("./pages/AdminApprovals"));
const AdminClubManagement = lazyWithRetry(() => import("./pages/AdminClubManagement"));
const AdminTeamManagement = lazyWithRetry(() => import("./pages/AdminTeamManagement"));
const AdminCoachManagement = lazyWithRetry(() => import("./pages/AdminCoachManagement"));
const AdminPlayerManagement = lazyWithRetry(() => import("./pages/AdminPlayerManagement"));
const AdminParentManagement = lazyWithRetry(() => import("./pages/AdminParentManagement"));
const AdminSkillsManagement = lazyWithRetry(() => import("./pages/AdminSkillsManagement"));
const AdminSkillsStrategy = lazyWithRetry(() => import("./pages/AdminSkillsStrategy"));
const AdminMediaManagement = lazyWithRetry(() => import("./pages/AdminMediaManagement"));
const AdminAudioManagement = lazyWithRetry(() => import("./pages/AdminAudioManagement"));
const AdminTournaments = lazyWithRetry(() => import("./pages/AdminTournaments"));
const AdminTournamentForm = lazyWithRetry(() => import("./pages/AdminTournamentForm"));
const AdminTournamentDetail = lazyWithRetry(() => import("./pages/AdminTournamentDetail"));
const AdminEvents = lazyWithRetry(() => import("./pages/AdminEvents"));
const AdminEventForm = lazyWithRetry(() => import("./pages/AdminEventForm"));
const AdminEventDetail = lazyWithRetry(() => import("./pages/AdminEventDetail"));
const AdminAwardBadges = lazyWithRetry(() => import("./pages/AdminAwardBadges"));
const AdminBadgeManagement = lazyWithRetry(() => import("./pages/AdminBadgeManagement"));
const AdminBackfillBadges = lazyWithRetry(() => import("./pages/AdminBackfillBadges"));
const AdminSkillBackfill = lazyWithRetry(() => import("./pages/AdminSkillBackfill"));
const AdminThumbnailMigration = lazyWithRetry(() => import("./pages/AdminThumbnailMigration"));
const AdminCamps = lazyWithRetry(() => import("./pages/AdminCamps"));
const AdminCampForm = lazyWithRetry(() => import("./pages/AdminCampForm"));
const AdminCampDetail = lazyWithRetry(() => import("./pages/AdminCampDetail"));
const AdminSoccerIQ = lazyWithRetry(() => import("./pages/AdminSoccerIQ"));
const AdminSoccerIQCreate = lazyWithRetry(() => import("./pages/AdminSoccerIQCreate"));
const AdminSoccerIQBulk = lazyWithRetry(() => import("./pages/AdminSoccerIQBulk"));
const AdminSoccerIQDetail = lazyWithRetry(() => import("./pages/AdminSoccerIQDetail"));
const AdminTerminologyBank = lazyWithRetry(() => import("./pages/AdminTerminologyBank"));

// Registration pages
const CoachRegister = lazyWithRetry(() => import("./pages/CoachRegister"));
const PlayerRegister = lazyWithRetry(() => import("./pages/PlayerRegister"));
const FindYourClub = lazyWithRetry(() => import("./pages/FindYourClub"));
const Events = lazyWithRetry(() => import("./pages/Events"));
const PublicEventDetail = lazyWithRetry(() => import("./pages/PublicEventDetail"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const ContactUs = lazyWithRetry(() => import("./pages/ContactUs"));

// Parent components
const ParentLayout = lazyWithRetry(() => import("./components/ParentLayout"));
const ParentPlayerLayout = lazyWithRetry(() => import("./components/parent/ParentPlayerLayout"));
const ParentAuth = lazyWithRetry(() => import("./pages/ParentAuth"));
const ParentDashboard = lazyWithRetry(() => import("./pages/ParentDashboard"));
const ParentPlayerDetail = lazyWithRetry(() => import("./pages/ParentPlayerDetail"));
const ParentPasswordReset = lazyWithRetry(() => import("./pages/ParentPasswordReset"));
const ParentEvents = lazyWithRetry(() => import("./pages/ParentEvents"));
const ParentEventDetail = lazyWithRetry(() => import("./pages/ParentEventDetail"));
const ParentEventsCalendar = lazyWithRetry(() => import("./pages/ParentEventsCalendar"));
const ParentIDP = lazyWithRetry(() => import("./pages/ParentIDP"));
const ParentSettings = lazyWithRetry(() => import("./pages/ParentSettings"));
const ParentActivity = lazyWithRetry(() => import("./pages/ParentActivity"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RealtimeProvider>
        <AuthProvider>
          <CoachAuthProvider>
            <AdminAuthProvider>
              <ParentAuthProvider>
                <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              
              {/* Find Your Club */}
              <Route 
                path="/find-club" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <FindYourClub />
                  </React.Suspense>
                } 
              />
              
              {/* Events Page */}
              <Route 
                path="/events" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Events />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/events/:eventId" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PublicEventDetail />
                  </React.Suspense>
                } 
              />
              
              {/* Privacy Policy */}
              <Route 
                path="/privacy-policy" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PrivacyPolicy />
                  </React.Suspense>
                } 
              />
              
              {/* Terms and Conditions */}
              <Route 
                path="/terms-conditions" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <TermsOfService />
                  </React.Suspense>
                } 
              />
              
              {/* Contact Us */}
              <Route 
                path="/contact-us" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ContactUs />
                  </React.Suspense>
                } 
              />
              
              {/* Registration Routes */}
              <Route 
                path="/register/coach/:clubSlug" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachRegister />
                  </React.Suspense>
                }
              />
              <Route 
                path="/register/player/:clubSlug" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerRegister />
                  </React.Suspense>
                }
              />
              
              {/* Player Routes */}
              <Route 
                path="/player" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerDashboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="session" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSession />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skill-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSkillStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="custom-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerCustomStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="train" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerTrain />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="train/:subcategoryId" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSubcategoryDetail />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="program" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerProgram />
                    </React.Suspense>
                  } 
                />
                {/* /player/profile now redirects to /player/avatar */}
                <Route 
                  path="profile" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerAvatar />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="avatar" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerAvatar />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="locker-room" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerLockerRoom />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="locker-room/:viewPlayerId" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerLockerRoom />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="career-roadmap" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerCareerRoadmap />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camp/:campId"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerCampLeaderboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="soccer-iq"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSoccerIQ />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="soccer-iq/category/:categoryName"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSoccerIQDetail />
                    </React.Suspense>
                  } 
                />
              </Route>
              
              {/* Player Soccer IQ Session (outside layout for fullscreen) */}
              <Route 
                path="/player/soccer-iq/session/:id"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerSoccerIQSession />
                  </React.Suspense>
                } 
              />
              
              {/* Player Skill Strategy Active Session Route */}
              <Route 
                path="/players/skill-strategy/active" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerSkillStrategyActive />
                  </React.Suspense>
                } 
              />
              
              <Route 
                path="/coach-auth"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachAuth />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/coach-test" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachTestPage />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/coach" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachDashboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="players" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachPlayerManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="teams" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachTeamManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skills-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachSkillsStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="settings" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachSettings />
                    </React.Suspense>
                  } 
                />
              </Route>
              {/* Parent Routes */}
              <Route 
                path="/parent/auth" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ParentAuth />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/parent/reset-password" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ParentPasswordReset />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/parent" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ParentLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <ParentDashboard />
                    </React.Suspense>
                  } 
                />
                {/* Per-player routes with tabs */}
                <Route 
                  path="player/:playerId" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <ParentPlayerLayout />
                    </React.Suspense>
                  }
                >
                  <Route index element={<React.Suspense fallback={<div>Loading...</div>}><ParentPlayerDetail /></React.Suspense>} />
                  <Route 
                    path="dashboard" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentPlayerDetail />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="activity" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentActivity />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="events" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentEvents />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="events/:eventId" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentEventDetail />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="calendar"
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentEventsCalendar />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="idp"
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentIDP />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="settings" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentSettings />
                      </React.Suspense>
                    } 
                  />
                </Route>
              </Route>

              <Route 
                path="/galleries" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Galleries />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/s3-folder/*" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <S3FolderDetail />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/gallery/:galleryId" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <GalleryDetail />
                  </React.Suspense>
                } 
              />
              {/* Admin Routes */}
              <Route 
                path="/admin/auth" 
                element={<AdminAuth />} 
              />
              <Route 
                path="/admin" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminDashboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="clubs" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminClubManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="teams" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminTeamManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="coaches" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCoachManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="players" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminPlayerManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="parents" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminParentManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skills" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSkillsManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skills-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSkillsStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="approvals" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminApprovals />
                    </React.Suspense>
                  } 
                />
                <Route
                  path="media"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminMediaManagement />
                    </React.Suspense>
                  } 
                 />
                 <Route
                   path="audio"
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminAudioManagement />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="users" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminUsers />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournaments />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments/new" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournamentForm />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments/:id/edit" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournamentForm />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments/:id" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournamentDetail />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEvents />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events/new" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEventForm />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events/:id" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEventDetail />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events/:id/edit" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEventForm />
                     </React.Suspense>
                   } 
                 />
                  <Route 
                    path="award-badges" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AdminAwardBadges />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="badges" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AdminBadgeManagement />
                      </React.Suspense>
                    } 
                  />
                <Route 
                  path="backfill-badges" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminBackfillBadges />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="backfill-skills" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSkillBackfill />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="thumbnail-migration" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminThumbnailMigration />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCamps />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps/new" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCampForm />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps/:id" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCampDetail />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps/:id/edit" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCampForm />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="soccer-iq" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSoccerIQ />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="soccer-iq/create" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSoccerIQCreate />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="soccer-iq/bulk" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSoccerIQBulk />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="soccer-iq/:id" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSoccerIQDetail />
                    </React.Suspense>
                  } 
                />
              </Route>
                <Route 
                  path="terminology" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminTerminologyBank />
                    </React.Suspense>
                  } 
                />
              <Route path="*" element={<NotFound />} />
            </Routes>
                </BrowserRouter>
                <VersionChecker />
                
                <Toaster />
                <Sonner />
              </ParentAuthProvider>
            </AdminAuthProvider>
          </CoachAuthProvider>
        </AuthProvider>
      </RealtimeProvider>
    </QueryClientProvider>
  );
}

export default App;